import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence, useScroll, useReducedMotion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useTranslation, useLang } from "@/lib/i18n";
import { usePostHog } from "@posthog/react";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastScrollY = useRef(0);
  const { scrollYProgress } = useScroll();
  const posthog = usePostHog();
  const prefersReduced = useReducedMotion();
  const t = useTranslation();
  const { lang } = useLang();
  const location = useLocation();

  // Build localised page hrefs
  const prefix = lang === "pl" ? "/pl" : "";
  const navLinks = [
    { label: t.nav.web3, href: `${prefix}/web3` },
    { label: t.nav.aiTeams, href: `${prefix}/ai-teams` },
    { label: t.nav.oneToOne, href: `${prefix}/1-1` },
  ];

  // Language-switch URL
  const switchLangHref = (() => {
    const path = location.pathname;
    if (lang === "en") return "/pl" + (path === "/" ? "" : path);
    return path.startsWith("/pl") ? path.slice(3) || "/" : "/";
  })();

  // Determine active nav link
  const activePath = location.pathname;

  const handleScroll = useCallback(() => {
    const y = window.scrollY;
    setScrolled(y > 50);
    if (y < 80) {
      setHidden(false);
    } else if (y > lastScrollY.current + 5 && !mobileOpen) {
      setHidden(true);
    } else if (y < lastScrollY.current - 5) {
      setHidden(false);
    }
    lastScrollY.current = y;
  }, [mobileOpen]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (href: string) =>
    activePath === href || activePath === href + "/";

  return (
    <>
      {/* Scroll progress bar */}
      {!prefersReduced && (
        <motion.div
          className="fixed top-0 left-0 right-0 h-[3px] bg-primary z-[60] origin-left pointer-events-none"
          style={{ scaleX: scrollYProgress, willChange: "transform", backfaceVisibility: "hidden" }}
        />
      )}

      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border/50" : ""
        } ${hidden ? "-translate-y-full" : "translate-y-0"}`}
      >
        <div className="container mx-auto flex items-center justify-between h-16 px-6">
          {/* Logo */}
          <a
            href={lang === "pl" ? "/pl" : "/"}
            className="font-display text-lg tracking-tight group min-h-[44px] inline-flex items-center"
          >
            Chris
            <span className="text-primary group-hover:text-gradient transition-all duration-300">
              Workflows
            </span>
          </a>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-12">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`relative text-base sm:text-lg font-display font-bold tracking-wide pb-1 border-b-2 transition-colors py-3 min-h-[44px] inline-flex items-center ${
                  isActive(link.href)
                    ? "text-primary border-primary"
                    : "text-soft-foreground hover:text-foreground border-transparent"
                }`}
              >
                {link.label}
              </a>
            ))}

            {/* Language switcher */}
            <a
              href={switchLangHref}
              className="text-xs font-body font-medium tracking-widest text-muted-foreground hover:text-foreground transition-colors border border-border/50 hover:border-border px-2.5 py-1 rounded min-h-[44px] inline-flex items-center"
            >
              {lang === "en" ? t.nav.pl : t.nav.en}
            </a>

            {/* CTA */}
            <a
              href={lang === "pl" ? "/pl/ai-teams#book" : "/ai-teams#book"}
              onClick={() => posthog?.capture("navbar_cta_clicked", { location: "desktop" })}
              className="ml-2 px-5 py-2 min-h-[44px] inline-flex items-center text-xs font-body font-semibold tracking-wider rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {t.nav.letsTalk}
            </a>
          </div>

          {/* Mobile: language switcher + burger */}
          <div className="lg:hidden flex items-center gap-2">
            <a
              href={switchLangHref}
              className="text-xs font-body font-medium tracking-widest text-muted-foreground hover:text-foreground transition-colors border border-border/50 px-2 py-1 rounded min-h-[44px] inline-flex items-center"
              aria-label="Switch language"
            >
              {lang === "en" ? t.nav.pl : t.nav.en}
            </a>
            <button
              className="text-foreground min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden bg-background/95 backdrop-blur-xl border-b border-border overflow-hidden"
            >
              <div className="flex flex-col gap-1 px-6 py-6">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={`flex flex-col gap-0.5 py-3 border-b border-border/30 last:border-0 transition-colors ${
                      isActive(link.href) ? "text-primary" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span className="text-base font-display font-semibold">{link.label}</span>
                  </a>
                ))}

                <a
                  href={switchLangHref}
                  onClick={() => setMobileOpen(false)}
                  className="text-sm font-body font-medium text-muted-foreground hover:text-foreground transition-colors py-3"
                >
                  {lang === "en" ? `🇵🇱 ${t.nav.pl}` : `🇬🇧 ${t.nav.en}`}
                </a>
                <a
                  href={lang === "pl" ? "/pl/ai-teams#book" : "/ai-teams#book"}
                  onClick={() => posthog?.capture("navbar_cta_clicked", { location: "mobile" })}
                  className="mt-2 px-5 py-2.5 text-sm font-body font-semibold rounded-xl bg-primary text-primary-foreground text-center"
                >
                  {t.nav.letsTalk}
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default Navbar;
